import { useEffect, useState } from "react";
import { X, Send, Loader2, Eye, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

function AddrField({ label, value, onChange, testid }) {
  const [sug, setSug] = useState([]);
  const last = value.split(/[,;]/).pop().trim();
  useEffect(() => {
    const t = setTimeout(() => api.get("/vesta/mail/contacts", { params: { q: last } }).then((r) => setSug(r.data.contacts)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [last]);
  const pick = (e) => { const parts = value.split(/[,;]/).slice(0, -1).map((x) => x.trim()).filter(Boolean); onChange([...parts, e, ""].join(", ")); };
  const used = new Set(split(value).map((x) => x.toLowerCase()));
  const list = sug.filter((c) => !used.has(c.email.toLowerCase())).slice(0, 6);
  return (
    <div className="group relative">
      <label className="flex items-center gap-3 border-b border-neutral-800 py-2">
        <span className="w-20 shrink-0 text-xs uppercase tracking-widest text-neutral-500">{label}</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="nome@azienda.it, …" className="w-full bg-transparent text-sm text-white outline-none" data-testid={testid} />
      </label>
      {!!list.length && (
        <div className="absolute left-20 right-0 top-full z-10 hidden max-h-48 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-950 py-1 shadow-xl group-focus-within:block" data-testid={`${testid}-suggestions`}>
          {list.map((c) => (
            <button key={c.email} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c.email); }} className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-white/5">
              <span className="truncate text-neutral-200">{c.name ? `${c.name} · ` : ""}<span className="text-neutral-400">{c.email}</span></span>
              <span className="shrink-0 text-[10px] uppercase text-[#C17F59]">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Composizione email «Ai VESTA»: destinatari, cc, oggetto, testo modificabile e anteprima dell'email reale
export function MailComposer({ draft, onClose, sendUrl = "/vesta/mail/send", sendParams, attachment }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async () => { const r = await api.post("/vesta/mail/preview", { subject, body, logo: !!attachment }); setPrev(r.data); };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(sendUrl, { to: split(to), cc: split(cc), subject, body }, { params: sendParams, timeout: 120000 });
      toast.success("Email inviata da Ai VESTA"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm" data-testid="vesta-mail-composer">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-neutral-800 bg-[#0d0d0d]">
        <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-3">
          <div><div className="font-heading text-lg font-extrabold text-[#E8B48F]">Nuova email</div><div className="text-[11px] text-neutral-500" data-testid="vesta-mail-from">Mittente: Ai VESTA · non riceve risposte</div></div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-neutral-400 hover:bg-white/10" data-testid="vesta-mail-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-1 overflow-y-auto px-5 py-3">
          <AddrField label="A" value={to} onChange={setTo} testid="vesta-mail-to" />
          <AddrField label="Cc" value={cc} onChange={setCc} testid="vesta-mail-cc" />
          <label className="flex items-center gap-3 border-b border-neutral-800 py-2">
            <span className="w-20 shrink-0 text-xs uppercase tracking-widest text-neutral-500">Oggetto</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-transparent text-sm text-white outline-none" data-testid="vesta-mail-subject" />
          </label>
          {attachment && <div className="flex items-center gap-2 border-b border-neutral-800 py-2 text-xs text-neutral-300" data-testid="vesta-mail-attachment"><Paperclip className="h-3.5 w-3.5 text-[#C17F59]" />{attachment}</div>}
          {prev ? (
            <div className="mt-3 overflow-hidden rounded-xl border border-neutral-800 bg-white" data-testid="vesta-mail-preview">
              <div className="border-b bg-neutral-100 px-3 py-1.5 text-[11px] text-neutral-600">Da: {prev.from} · A: {to || "—"}{cc ? ` · Cc: ${cc}` : ""}</div>
              <iframe title="Anteprima email" srcDoc={prev.html} className="h-[46vh] w-full" sandbox="allow-same-origin" />
            </div>
          ) : (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="mt-3 w-full resize-y rounded-xl border border-neutral-800 bg-black/40 p-3 text-sm leading-relaxed text-neutral-200 outline-none focus:border-[#C17F59]" data-testid="vesta-mail-body" />
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-neutral-800 px-5 py-3">
          <button type="button" onClick={() => (prev ? setPrev(null) : preview())} className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-4 py-2 text-xs font-bold uppercase tracking-widest text-neutral-300 hover:border-[#C17F59]" data-testid="vesta-mail-preview-btn">
            <Eye className="h-4 w-4" />{prev ? "Modifica" : "Anteprima"}
          </button>
          <button type="button" onClick={send} disabled={busy || !split(to).length} className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-5 py-2 text-xs font-bold uppercase tracking-widest text-black transition-transform hover:scale-105 disabled:opacity-40" data-testid="vesta-mail-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Invia
          </button>
        </div>
      </div>
    </div>
  );
}
